// src/components/MouseFollower.jsx
import { useEffect, useRef, useState } from "react";
import gsap from "gsap";

const MouseFollower = () => {
  const followerRef = useRef(null);
  const ringRef = useRef(null);
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    const checkDevice = () => {
      setIsDesktop(
        window.matchMedia("(pointer: fine)").matches &&
          window.innerWidth >= 768
      );
    };

    checkDevice();
    window.addEventListener("resize", checkDevice);

    return () => window.removeEventListener("resize", checkDevice);
  }, []);

  useEffect(() => {
    if (!isDesktop) return;

    const follower = followerRef.current;
    const ring = ringRef.current;

    if (!follower || !ring) return;

    gsap.set([follower, ring], {
      xPercent: -50,
      yPercent: -50,
      x: -100,
      y: -100,
      opacity: 0,
      scale: 1,
    });

    const followerQuickX = gsap.quickTo(follower, "x", {
      duration: 0.18,
      ease: "power3.out",
    });

    const followerQuickY = gsap.quickTo(follower, "y", {
      duration: 0.18,
      ease: "power3.out",
    });

    const ringQuickX = gsap.quickTo(ring, "x", {
      duration: 0.38,
      ease: "power3.out",
    });

    const ringQuickY = gsap.quickTo(ring, "y", {
      duration: 0.38,
      ease: "power3.out",
    });

    const handleMouseMove = (e) => {
      followerQuickX(e.clientX);
      followerQuickY(e.clientY);
      ringQuickX(e.clientX);
      ringQuickY(e.clientY);

      gsap.to([follower, ring], {
        opacity: 1,
        duration: 0.25,
        ease: "power2.out",
      });
    };

    const handleMouseLeaveWindow = () => {
      gsap.to([follower, ring], {
        opacity: 0,
        duration: 0.25,
        ease: "power2.out",
      });
    };

    const handleMouseEnterInteractive = () => {
      gsap.to(follower, {
        scale: 0.75,
        backgroundColor: "#ffffff",
        duration: 0.2,
        ease: "power2.out",
      });

      gsap.to(ring, {
        scale: 1.8,
        borderColor: "#a83417",
        opacity: 0.8,
        duration: 0.25,
        ease: "power2.out",
      });
    };

    const handleMouseLeaveInteractive = () => {
      gsap.to(follower, {
        scale: 1,
        backgroundColor: "#026576",
        duration: 0.2,
        ease: "power2.out",
      });

      gsap.to(ring, {
        scale: 1,
        borderColor: "#026576",
        opacity: 0.5,
        duration: 0.25,
        ease: "power2.out",
      });
    };

    const interactiveElements = document.querySelectorAll(
      "a, button, input, textarea, select, [role='button'], .hover-target"
    );

    window.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseleave", handleMouseLeaveWindow);

    interactiveElements.forEach((el) => {
      el.addEventListener("mouseenter", handleMouseEnterInteractive);
      el.addEventListener("mouseleave", handleMouseLeaveInteractive);
    });

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseleave", handleMouseLeaveWindow);

      interactiveElements.forEach((el) => {
        el.removeEventListener("mouseenter", handleMouseEnterInteractive);
        el.removeEventListener("mouseleave", handleMouseLeaveInteractive);
      });
    };
  }, [isDesktop]);

  if (!isDesktop) return null;

  return (
    <>
      <div
        ref={ringRef}
        className="pointer-events-none fixed left-0 top-0 z-[9998] h-12 w-12 rounded-full border-2 border-three-color opacity-50 mix-blend-difference shadow-[0_0_30px_rgba(2,101,118,0.35)] dark:border-cyan-300"
      />

      <div
        ref={followerRef}
        className="pointer-events-none fixed left-0 top-0 z-[9999] h-3.5 w-3.5 rounded-full bg-three-color mix-blend-difference shadow-[0_0_18px_rgba(2,101,118,0.75)] dark:bg-cyan-300"
      />
    </>
  );
};

export default MouseFollower;